import { modelUtils } from '@pelicargo/utils';

const { CUSTOMER_ASSET_SLOT_KEYS, CUSTOMER_COPY_DEFAULTS, CUSTOMER_COPY_KEYS } = modelUtils.customerTheme;

type CustomerAssetSlot = modelUtils.customerTheme.CustomerAssetSlot;

export const COLOR_SCALE_STEPS = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'] as const;

const HEADING_LEVELS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;

/** Matches the `textTransform` enum in packages/utils/src/models/customer-theme/schema.ts. */
export const TEXT_TRANSFORM_OPTIONS = [
  { label: 'none', value: 'none' },
  { label: 'uppercase', value: 'uppercase' },
  { label: 'lowercase', value: 'lowercase' },
  { label: 'capitalize', value: 'capitalize' },
];

export type ThemeFieldKind = 'color' | 'css' | 'transform' | 'copy' | 'asset';

export type ThemeField = {
  /** react-hook-form field name. Mirrors the schema path, except for copy/assets — see `form.ts`. */
  name: string;
  label: string;
  kind: ThemeFieldKind;
  placeholder?: string;
  helper?: string;
  /** Only on `asset` fields. The control needs the slot to know its media kind and default. */
  slot?: CustomerAssetSlot;
};

export type ThemeFieldGroup = {
  title?: string;
  fields: ThemeField[];
};

export type ThemeSectionName =
  | 'colors'
  | 'semantic'
  | 'fonts'
  | 'radii'
  | 'heading'
  | 'button'
  | 'field'
  | 'input'
  | 'label'
  | 'switch'
  | 'icon'
  | 'logo'
  | 'copy'
  | 'assets';

export type ThemeSection = {
  name: ThemeSectionName;
  title: string;
  description: string;
  groups: ThemeFieldGroup[];
};

const css = (name: string, label: string, helper?: string): ThemeField => ({ name, label, kind: 'css', helper });

const color = (name: string, label: string): ThemeField => ({ name, label, kind: 'color' });

const transform = (name: string): ThemeField => ({ name, label: 'Text transform', kind: 'transform' });

const colorScale = (prefix: string): ThemeField[] => COLOR_SCALE_STEPS.map((step) => color(`${prefix}.${step}`, step));

const headingLevelGroup = (level: (typeof HEADING_LEVELS)[number]): ThemeFieldGroup => ({
  title: level.toUpperCase(),
  fields: [
    css(`heading.${level}.fontSize`, 'Font size'),
    css(`heading.${level}.lineHeight`, 'Line height'),
    css(`heading.${level}.fontWeight`, 'Font weight'),
  ],
});

// Only the slots the customer app actually reads are offered, and each carries its built-in
// default as the placeholder so staff can see what they are overriding before typing over it.
const COPY_FIELDS: ThemeField[] = CUSTOMER_COPY_KEYS.map((key, index) => ({
  name: `copyOverrides.${index}`,
  label: key,
  kind: 'copy',
  placeholder: CUSTOMER_COPY_DEFAULTS[key],
}));

// The slot rides along on the field because the control derives everything else from it: which
// MIME types to accept, whether the preview is an image or a video, and the default it is
// overriding.
const ASSET_FIELDS: ThemeField[] = CUSTOMER_ASSET_SLOT_KEYS.map((slot, index) => ({
  name: `assetOverrides.${index}`,
  label: slot,
  kind: 'asset',
  slot,
}));

export const THEME_SECTIONS = [
  {
    name: 'colors',
    title: 'Brand colours',
    description: 'The primary and secondary Chakra colour scales. Any CSS colour is accepted.',
    groups: [
      { title: 'Primary', fields: colorScale('colors.primary') },
      { title: 'Secondary', fields: colorScale('colors.secondary') },
    ],
  },
  {
    name: 'semantic',
    title: 'Semantic colours',
    description: 'Surfaces, borders, text and status colours used across the customer app.',
    groups: [
      {
        title: 'Surfaces',
        fields: [
          color('semantic.bgSurface', 'Surface background'),
          color('semantic.bgDefault', 'Default background'),
          color('semantic.bgSubtle', 'Subtle background'),
          color('semantic.bgInverse', 'Inverse background'),
          color('semantic.textInverse', 'Inverse text'),
        ],
      },
      {
        title: 'Borders',
        fields: [
          color('semantic.borderLight', 'Light'),
          color('semantic.borderDefault', 'Default'),
          color('semantic.borderHover', 'Hover'),
          color('semantic.borderFocused', 'Focused'),
        ],
      },
      {
        title: 'Text',
        fields: [
          color('semantic.textDefault', 'Default'),
          color('semantic.textParagraph', 'Paragraph'),
          color('semantic.textSubtle', 'Subtle'),
          color('semantic.textMuted', 'Muted'),
          color('semantic.textPlaceholder', 'Placeholder'),
        ],
      },
      {
        title: 'Status',
        fields: [
          color('semantic.error', 'Error'),
          color('semantic.success', 'Success'),
          color('semantic.warning', 'Warning'),
          color('semantic.info', 'Info'),
        ],
      },
    ],
  },
  {
    name: 'fonts',
    title: 'Fonts',
    description: 'A family is inert unless the face is loaded, so set the stylesheet URL as well as the families.',
    groups: [
      {
        fields: [
          css('fonts.heading', 'Heading family'),
          css('fonts.body', 'Body family'),
          css('fonts.url', 'Stylesheet URL', 'Injected as a stylesheet link; must be a full URL'),
        ],
      },
    ],
  },
  {
    name: 'heading',
    title: 'Headings',
    description: 'Shared heading styling, then per-level sizing.',
    groups: [
      {
        title: 'Shared',
        fields: [
          css('heading.fontFamily', 'Font family'),
          css('heading.fontWeight', 'Font weight'),
          css('heading.letterSpacing', 'Letter spacing'),
          transform('heading.textTransform'),
          color('heading.color', 'Colour'),
        ],
      },
      ...HEADING_LEVELS.map(headingLevelGroup),
    ],
  },
  {
    name: 'radii',
    title: 'Corner radius',
    description:
      'The shared radius scale. Overriding one entry reshapes every surface that uses it - cards, modals, tags, popovers. The per-component radii below override just that component.',
    groups: [
      {
        fields: [
          css('radii.none', 'None'),
          css('radii.sm', 'Small'),
          css('radii.base', 'Base'),
          css('radii.md', 'Medium'),
          css('radii.lg', 'Large'),
          css('radii.xl', 'Extra large'),
          css('radii.2xl', '2x large'),
          css('radii.3xl', '3x large'),
          css('radii.full', 'Full (pill)'),
        ],
      },
    ],
  },
  {
    name: 'button',
    title: 'Buttons',
    description: 'Button chrome and the underline used by link-style buttons.',
    groups: [
      {
        fields: [
          css('button.borderRadius', 'Border radius'),
          css('button.fontWeight', 'Font weight'),
          css('button.letterSpacing', 'Letter spacing'),
          transform('button.textTransform'),
          color('button.underlineColor', 'Underline colour'),
        ],
      },
    ],
  },
  {
    name: 'field',
    title: 'Fields',
    description: 'The wrapper drawn around every form control.',
    groups: [
      {
        title: 'Box',
        fields: [
          css('field.borderRadius', 'Border radius'),
          css('field.borderWidth', 'Border width'),
          css('field.padding', 'Padding'),
          css('field.focusRing', 'Focus ring'),
        ],
      },
      {
        title: 'Colours',
        fields: [
          color('field.bg', 'Background'),
          color('field.borderColor', 'Border'),
          color('field.borderColorHover', 'Border (hover)'),
          color('field.borderColorFocus', 'Border (focus)'),
        ],
      },
    ],
  },
  {
    name: 'input',
    title: 'Inputs',
    description: 'The control inside the field wrapper.',
    groups: [
      {
        fields: [
          css('input.fontSize', 'Font size'),
          css('input.borderRadius', 'Border radius'),
          color('input.placeholderColor', 'Placeholder colour'),
        ],
      },
    ],
  },
  {
    name: 'label',
    title: 'Labels',
    description: 'Form label typography.',
    groups: [
      {
        fields: [
          css('label.fontSize', 'Font size'),
          css('label.fontWeight', 'Font weight'),
          css('label.letterSpacing', 'Letter spacing'),
          transform('label.textTransform'),
        ],
      },
    ],
  },
  {
    name: 'switch',
    title: 'Switches',
    description:
      'The Yes/No pill groups. The selected pill was the same dark grey everywhere regardless of brand, so this is usually the first thing to set after the colour scales.',
    groups: [
      {
        fields: [
          color('switch.bg', 'Track background'),
          color('switch.optionBg', 'Unselected option'),
          color('switch.color', 'Unselected label'),
          color('switch.selectedBg', 'Selected option'),
          color('switch.selectedColor', 'Selected label'),
          css('switch.borderRadius', 'Border radius'),
        ],
      },
    ],
  },
  {
    name: 'icon',
    title: 'Icons',
    description: 'Icon colours that are neither text nor a surface.',
    groups: [
      {
        fields: [
          {
            name: 'icon.infoBg',
            label: 'Info icon',
            kind: 'color',
            helper: 'The filled disc; the "i" is knocked out of it and shows the surface behind',
          },
        ],
      },
    ],
  },
  {
    name: 'logo',
    title: 'Logo sizing',
    description: 'Applied as layout props rather than CSS variables. A digits-only value is sent as px.',
    groups: [
      {
        fields: [
          css('logo.width', 'Width', 'Number (px) or CSS length'),
          css('logo.height', 'Height', 'Number (px) or CSS length'),
          css('logo.fullWidth', 'Full width', 'Number (px) or CSS length'),
          css('logo.fullHeight', 'Full height', 'Number (px) or CSS length'),
        ],
      },
    ],
  },
  {
    name: 'copy',
    title: 'Copy',
    description:
      'Text content, so unlike styling it cannot ride a CSS variable and a value set here visibly swaps in after first paint. Permanent wording belongs in the tenant definition; this is for tweaks.',
    groups: [{ fields: COPY_FIELDS }],
  },
  {
    name: 'assets',
    title: 'Assets',
    description:
      'Re-point an image or video slot without a deploy, or remove one entirely. Upload a file, or paste a URL if it is already hosted somewhere. An upload only fills in the field - nothing reaches the customer app until you save.',
    groups: [{ fields: ASSET_FIELDS }],
  },
] satisfies ThemeSection[];
